/**
 * Projects Page
 *
 * List and manage projects with cost tracking.
 * Features project selector (combobox) with stats and cost evolution chart.
 * Unified view for mobile and desktop with responsive sizing.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useProjectStore } from '../store/projectStore';
import { useAuthStore } from '../store/authStore';
import { Card, Button, Modal } from '../components/ui';
import { formatCurrency, formatDate } from '../utils/formatters';
import type { ProjectWithStats, ProjectCostDataPoint } from '@budgira/shared';

// Status badge colors
const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  archived: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400',
};

const ProjectsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canManageProjects } = useAuthStore();
  const canEdit = canManageProjects();
  const {
    projects,
    costEvolution,
    isLoading,
    isLoadingCostEvolution,
    error,
    includeArchived,
    fetchProjects,
    fetchCostEvolution,
    deleteProject,
    archiveProject,
    completeProject,
    reactivateProject,
    setIncludeArchived,
    clearError,
  } = useProjectStore();

  // Selected project for detail view
  // Reset when projects list changes and selected project is no longer in list
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const selectedProject = selectedProjectId 
    ? projects.find(p => p.id === selectedProjectId) ?? null 
    : null;
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<ProjectWithStats | null>(null);

  useEffect(() => {
    // Clear any previous error when mounting the page
    clearError();
    fetchProjects();
  }, [fetchProjects, includeArchived, clearError]);

  // Refresh projects when page becomes visible (e.g., returning from operation form)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchProjects();
      }
    };
    
    const handleFocus = () => {
      fetchProjects();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchProjects]);

  // Auto-select first project when projects load
  // Reset selection if current selection is not in the new list
  useEffect(() => {
    if (projects.length > 0) {
      // Check if current selection is still valid
      if (!selectedProjectId || !projects.some(p => p.id === selectedProjectId)) {
        setSelectedProjectId(projects[0]?.id ?? null);
      }
    } else {
      setSelectedProjectId(null);
    }
  }, [projects, selectedProjectId]);

  // Fetch cost evolution when a project is selected
  // Only fetch if the project exists in the current projects list
  useEffect(() => {
    if (selectedProject) {
      fetchCostEvolution(selectedProject.id);
    }
  }, [selectedProject, fetchCostEvolution]);

  // Handle project selection from combobox
  const handleProjectSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const projectId = e.target.value;
    setSelectedProjectId(projectId || null);
  };

  const handleDeleteClick = (project: ProjectWithStats) => {
    setProjectToDelete(project);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (projectToDelete) {
      await deleteProject(projectToDelete.id);
      setDeleteModalOpen(false);
      setProjectToDelete(null);
      if (selectedProjectId === projectToDelete.id) {
        setSelectedProjectId(null);
      }
    }
  };

  const handleArchive = async (project: ProjectWithStats) => {
    await archiveProject(project.id);
  };

  const handleComplete = async (project: ProjectWithStats) => {
    await completeProject(project.id);
  };

  const handleReactivate = async (project: ProjectWithStats) => {
    await reactivateProject(project.id);
  };

  // Format chart data
  const chartData = costEvolution.map((point) => ({
    ...point,
    displayCost: Math.abs(point.cumulativeCost),
    formattedDate: formatDate(point.date, 'short'),
  }));

  // Custom tooltip for the chart
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: ProjectCostDataPoint & { displayCost: number } }>;
  }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0]?.payload;
      if (!data) return null;
      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {formatDate(data.date, 'long')}
          </p>
          {data.label && (
            <p className="text-sm font-medium text-gray-900 dark:text-white">{data.label}</p>
          )}
          <p className="text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              {t('projects.chart.operation')}:{' '}
            </span>
            <span
              className={data.amount < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}
            >
              {formatCurrency(data.amount)}
            </span>
          </p>
          <p className="text-sm font-medium">
            <span className="text-gray-600 dark:text-gray-400">
              {t('projects.chart.cumulative')}:{' '}
            </span>
            <span className="text-gray-900 dark:text-white">
              {formatCurrency(Math.abs(data.cumulativeCost))}
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-2 sm:space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t('projects.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm mt-1">
            {t('projects.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-violet-600 focus:ring-violet-500"
            />
            <span className="hidden sm:inline">{t('projects.showArchived')}</span>
            <span className="sm:hidden">Archivés</span>
          </label>
          {canEdit && (
            <>
              <Button onClick={() => navigate('/projects/new')} size="sm" className="sm:hidden">
                {t('projects.createNew')}
              </Button>
              <Button onClick={() => navigate('/projects/new')} className="hidden sm:inline-flex">
                {t('projects.createNew')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={clearError}
            className="text-sm text-red-500 hover:text-red-700 dark:hover:text-red-300 mt-2"
          >
            {t('common.dismiss')}
          </button>
        </div>
      )}

      {/* Main Content */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
        </div>
      ) : projects.length === 0 ? (
        <Card className="p-8 text-center max-w-md mx-auto">
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {t('projects.noProjects')}
          </p>
          <Button onClick={() => navigate('/projects/new')}>
            {t('projects.createFirst')}
          </Button>
        </Card>
      ) : (
        <div className="space-y-4 md:space-y-6">
          {/* Project Selector (Combobox) */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              {selectedProject?.color && (
                <div
                  className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full z-10"
                  style={{ backgroundColor: selectedProject.color }}
                />
              )}
              <select
                value={selectedProject?.id || ''}
                onChange={handleProjectSelect}
                className={`w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white py-2 sm:py-2.5 pr-8 sm:pr-10 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm sm:text-base ${
                  selectedProject?.color ? 'pl-7 sm:pl-10' : 'pl-2 sm:pl-3'
                }`}
              >
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {projects.length} projet{projects.length > 1 ? 's' : ''}
            </span>
          </div>

          {/* Project Detail */}
          {selectedProject && (
            <Card noPadding className="p-1.5 sm:p-4 md:p-6">
              {/* Header with status and actions */}
              <div className="mb-3 sm:mb-4">
                {/* Desktop: title and buttons on same row */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  {/* Title, status and description */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 sm:gap-3">
                      {selectedProject.color && (
                        <div
                          className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 rounded-full flex-shrink-0"
                          style={{ backgroundColor: selectedProject.color }}
                        />
                      )}
                      <h2 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                        {selectedProject.name}
                      </h2>
                      <span
                        className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full flex-shrink-0 ${STATUS_COLORS[selectedProject.status]}`}
                      >
                        {t(`projects.status.${selectedProject.status}`)}
                      </span>
                    </div>
                    {/* Description - below title */}
                    {selectedProject.description && (
                      <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {selectedProject.description}
                      </p>
                    )}
                  </div>
                  {/* Action buttons - below description on mobile, right side on desktop */}
                  {canEdit && (
                    <div className="flex items-center gap-1 flex-shrink-0 mt-2 sm:mt-0">
                      {/* Edit button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/projects/${selectedProject.id}/edit`)}
                        className="px-1.5 sm:px-3"
                        title={t('common.edit')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span className="hidden sm:inline ml-1.5">{t('common.edit')}</span>
                      </Button>
                      
                      {/* Complete/Reactivate button */}
                      {selectedProject.status === 'active' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleComplete(selectedProject)}
                          className="px-1.5 sm:px-3 text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          title={t('projects.actions.complete')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="hidden sm:inline ml-1.5">{t('projects.actions.complete')}</span>
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReactivate(selectedProject)}
                          className="px-1.5 sm:px-3 text-green-600 dark:text-green-400 border-green-300 dark:border-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                          title={t('projects.actions.reactivate')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          <span className="hidden sm:inline ml-1.5">{t('projects.actions.reactivate')}</span>
                        </Button>
                      )}
                      
                      {/* Archive button (only for completed projects) */}
                      {selectedProject.status === 'completed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleArchive(selectedProject)}
                          className="px-1.5 sm:px-3 text-gray-600 dark:text-gray-400"
                          title={t('projects.actions.archive')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                          </svg>
                          <span className="hidden sm:inline ml-1.5">{t('projects.actions.archive')}</span>
                        </Button>
                      )}
                      
                      {/* Delete button - only show if no operations are linked */}
                      {selectedProject.operationCount === 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteClick(selectedProject)}
                          className="px-1.5 sm:px-3 text-red-600 dark:text-red-400 border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          title={t('common.delete')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span className="hidden sm:inline ml-1.5">{t('common.delete')}</span>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-1 sm:gap-2 md:gap-4 p-2 sm:p-3 md:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mb-3 sm:mb-4">
                <div className="text-center">
                  <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-500 dark:text-gray-400">
                    {t('projects.stats.debits')}
                  </p>
                  <p className="text-[10px] sm:text-xs md:text-base font-semibold text-red-600 dark:text-red-400">
                    {formatCurrency(selectedProject.totalDebits)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-500 dark:text-gray-400">
                    {t('projects.stats.credits')}
                  </p>
                  <p className="text-[10px] sm:text-xs md:text-base font-semibold text-green-600 dark:text-green-400">
                    {formatCurrency(selectedProject.totalCredits)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-500 dark:text-gray-400">
                    {t('projects.stats.net')}
                  </p>
                  <p
                    className={`text-[10px] sm:text-xs md:text-base font-semibold ${
                      selectedProject.netAmount >= 0
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {formatCurrency(selectedProject.netAmount)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-500 dark:text-gray-400">
                    {t('projects.stats.operations')}
                  </p>
                  <p className="text-[10px] sm:text-xs md:text-base font-semibold text-gray-900 dark:text-white">
                    {selectedProject.operationCount}
                  </p>
                </div>
              </div>

              {/* Budget Progress */}
              {selectedProject.maxBudget && (
                <div className="p-2 sm:p-3 md:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mb-3 sm:mb-4">
                  <div className="flex justify-between text-gray-600 dark:text-gray-400 mb-1">
                    <span style={{ fontSize: '10px' }} className="sm:text-xs">{t('projects.budget')}</span>
                    <span style={{ fontSize: '10px' }} className="sm:text-xs">
                      {(selectedProject.budgetUsagePercent ?? 0).toFixed(0)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 sm:h-2">
                    <div
                      className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                        (selectedProject.budgetUsagePercent ?? 0) > 100
                          ? 'bg-red-500'
                          : (selectedProject.budgetUsagePercent ?? 0) > 80
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(selectedProject.budgetUsagePercent ?? 0, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-gray-500 dark:text-gray-500 mt-1">
                    <span style={{ fontSize: '10px' }} className="sm:text-xs">{formatCurrency(selectedProject.totalDebits)}</span>
                    <span style={{ fontSize: '10px' }} className="sm:text-xs">{formatCurrency(selectedProject.maxBudget)}</span>
                  </div>
                </div>
              )}

              {/* Cost Evolution Chart */}
              <div>
                <h3 className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                  {t('projects.chart.title')}
                </h3>

                {isLoadingCostEvolution ? (
                  <div className="flex justify-center py-6 sm:py-8">
                    <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-violet-600" />
                  </div>
                ) : chartData.length === 0 ? (
                  <div className="text-center py-6 sm:py-8 text-gray-500 dark:text-gray-400 text-xs sm:text-sm">
                    {t('projects.chart.noData')}
                  </div>
                ) : (
                  <div className="h-40 sm:h-48 md:h-64 lg:h-80 -mx-2 sm:mx-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 5, right: 2, left: -5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                        <XAxis
                          dataKey="formattedDate"
                          tick={{ fontSize: 9 }}
                          className="text-gray-600 dark:text-gray-400"
                        />
                        <YAxis
                          tick={{ fontSize: 9 }}
                          tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}k€` : `${value}€`}
                          className="text-gray-600 dark:text-gray-400"
                          width={40}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        {selectedProject.maxBudget && (
                          <ReferenceLine
                            y={selectedProject.maxBudget}
                            stroke="#ef4444"
                            strokeDasharray="5 5"
                            label={{
                              value: t('projects.chart.budgetLimit'),
                              position: 'insideTopRight',
                              fill: '#ef4444',
                              fontSize: 9,
                            }}
                          />
                        )}
                        <Line
                          type="monotone"
                          dataKey="displayCost"
                          stroke="#8b5cf6"
                          strokeWidth={2}
                          dot={{ fill: '#8b5cf6', strokeWidth: 1, r: 2 }}
                          activeDot={{ r: 4, fill: '#7c3aed' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setProjectToDelete(null);
        }}
        title={t('projects.deleteConfirm.title')}
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            {t('projects.deleteConfirm.message', { name: projectToDelete?.name })}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            {t('projects.deleteConfirm.warning')}
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setProjectToDelete(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm}>
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectsPage;
